import React from 'react';

interface ErrorBoundaryProps {
  fallback: React.ReactNode | ((error: Error) => React.ReactNode);
  children: React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * ISS-063 — React commit 단계의 throw 를 catch 하여 페이지 흰 화면을 막는다.
 *
 * 한계 (catch 못함):
 * - 이벤트 핸들러 / setTimeout / Promise rejection 안의 throw
 * - SSR 단계의 에러
 *
 * 본 프로젝트의 주 사용처는 외부 SDK (Naver Maps) 가 React lifecycle 안에서 throw 하는
 * 경우의 격리. SDK callback 내부 throw 는 호출자 (MapView) 의 try/catch 가 담당.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (this.props.onError) {
      try { this.props.onError(error, info); } catch { /* 옵션 핸들러 격리 */ }
    }
  }

  render(): React.ReactNode {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      return typeof fallback === 'function' ? fallback(error) : fallback;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
